import React, { useMemo, useCallback, useState } from 'react'

import dayjs from 'dayjs'
import { PlayCircleOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import classnames from 'classnames'
import useReducers from '@/store/long-creation'
import TextRender from '@/components/TextRender'
import InsufficientStatus from '@/components/InsufficientStatus'
import http from '@/utils/request'

import useCheckLongCreationPending from '@/hooks/useCheckLongCreationPending'
import { parseStream } from '@/utils/tryParse'
import tryJson from '@/utils/tryJson'
import topup from '@/utils/topup'

import styles from './Article.module.scss'
import commonStyles from '../../long-creation.module.scss'

export default React.memo(function Article() {
  const [state, actions] = useReducers()
  const [loading, setLoading] = useState(false)
  const [checkLoading, setCheckLoading] = useState(false)

  useCheckLongCreationPending('SECTION', 5000)

  const messages = useMemo(() => {
    return state.messages[state.active] || []
  }, [state.messages, state.active])

  const hasInsufficient = useMemo(() => {
    return messages.some(item => item.role === 'assistant' && item.type === 'SECTION' && item.status === 'DONE_WITH_NOT_SUFFICIENT_FUNDS')
  }, [messages])

  const onCheckFunds = useCallback(async () => {
    await actions.checkFunds({ chatId: state.active, type: 'SECTION' })
  }, [state.active])

  const createSection = useCallback(async (index) => {
    if (hasInsufficient) {
      topup()
      return
    }

    const chatId = state.active

    try {
      setLoading(true)
      await http.post(`/api/chat/long-creation/${chatId}/article`, { index }, {
        onDownloadProgress: function ({ event }) {
          const xhr = event.target
          const { responseText } = xhr
          if (xhr.status !== 200) {
            return
          }

          // const jsonText = `[${responseText.replace(/\}\{/g, '},{')}]`
          const json = parseStream(responseText)

          if (!json) {
            return
          }

          const content = json.reduce((result, item) => {
            return `${result}${item.data}`
          }, '')

          actions.setStreams({
            [`${chatId}-${index}`]: content
          })
        },
      })

    } catch(err) {
      // actions.setMessages({ [chatId]: messages })
      if (err.response?.data.code === '1001') {
        topup()
      }
    }

    actions.getChatMessages(chatId)
    setLoading(false)
  }, [state.active, hasInsufficient])

  const title = useMemo(() => {
    const titleMessage = messages.find(item => item.role === 'user' && item.type === 'TITLE')

    if (!titleMessage) {
      return null
    }

    return (
      <h3 className={styles.title}>{titleMessage.content}</h3>
    )
  }, [state.messages, state.active])

  const getSectionContent = useCallback((index) => {
    const sectionMessage = messages.find(item => {
      if (item.type !== 'SECTION' || item.role !== 'assistant' || !item.extra) {
        return false
      }

      const extra = tryJson(item.extra)

      return extra?.index === index
    })

    return sectionMessage
  }, [messages])

  const content = useMemo(() => {
    const outlineMessage = messages.find(item => item.role === 'user' && item.type === 'OUTLINE')

    if (!outlineMessage) {
      return null
    }

    const outline = tryJson(outlineMessage.content)
    const hasPending = messages.some(item => item.role === 'assistant' && item.type === 'SECTION' && item.status === 'PENDING')

    const chatId = state.active

    return outline.map((line, index) => {
      const sectionMessage = getSectionContent(index)

      const sectionContent = sectionMessage?.content || state.streams[`${chatId}-${index}`] || ''

      function isTimeout() {
        const date = dayjs(sectionMessage?.updated_at)
        const duration = Date.now() - date.toDate().getTime()
        return duration / 1000 / 60 / 10 > 1
      }

      function canCreate() {
        if (!sectionMessage || sectionMessage?.status === 'REMOTE_ERROR') {
          return true
        }

        if (sectionMessage?.status === 'PENDING') {
          return isTimeout()
        }

        return false
      }

      return (
        <div key={index} className={styles.section}>
          <h4>
            {index + 1}. {line}

            {
              canCreate() &&
              <Button loading={loading || hasPending} size="small" type="primary" onClick={() => createSection(index)}>
                <PlayCircleOutlined />生成章节
              </Button>
            }
          </h4>

          {
            sectionContent &&
            <InsufficientStatus onCheckFunds={onCheckFunds} status={sectionMessage?.status} tip="章节已生成，余额不足，请充值后进行查看">
              <TextRender text={sectionContent}/>
            </InsufficientStatus>
          }

          {
            !sectionContent && sectionMessage?.status === 'PENDING' && !isTimeout() &&
            <div className={styles.pending}>正在生成章节...</div>
          }
          {
            !sectionContent && (sectionMessage?.status === 'REMOTE_ERROR' || isTimeout()) &&
            <div className={styles.error}>章节生成失败，请重试...</div>
          }
        </div>
      )
    })
  }, [messages, createSection, getSectionContent, loading, state.streams, hasInsufficient, onCheckFunds])

  if (!content) {
    return null
  }

  return (
    <div className={classnames(commonStyles.message, commonStyles.assistant)}>
      { title }
      { content }
    </div>
  )
})
